import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useDateFormat } from '../contexts/DateFormatContext';
import DateInput from '../components/DateInput';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Price from '../components/Price';
import { 
  DollarSign, 
  TrendingUp, 
  Users, 
  CreditCard, 
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
  ArrowLeft,
  Download,
  FileText,
  FileSpreadsheet,
  Printer,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPayments = () => {
  const { user } = useContext(AuthContext);
  const { formatDate } = useDateFormat();
  const navigate = useNavigate();
  
  const [payments, setPayments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [productType, setProductType] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/admin/dashboard');
      return;
    }
    fetchPayments();
  }, [user, currentPage, navigate]);

  useEffect(() => {
    // Close export menu when clicking outside
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('[data-testid="export-menu-button"]')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showExportMenu]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const params = {
        page: currentPage,
        limit: 10
      };
      
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (productType) params.product_type = productType;
      if (searchUser) params.user_id = searchUser;
      if (ownerEmail) params.owner_email = ownerEmail;
      if (serviceName) params.service_name = serviceName;
      
      const response = await axios.get(`${API}/admin/payments`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPayments(response.data.payments);
      setStatistics(response.data.statistics);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchPayments();
  };

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    setProductType('');
    setSearchUser('');
    setOwnerEmail('');
    setServiceName('');
    setCurrentPage(1);
    setTimeout(() => fetchPayments(), 100);
  };

  const handleExport = async (format = 'csv') => {
    setShowExportMenu(false);
    setExporting(true);
    
    try {
      const token = localStorage.getItem('session_token');
      const params = {};
      
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (productType) params.product_type = productType;
      if (searchUser) params.user_id = searchUser;
      if (ownerEmail) params.owner_email = ownerEmail;
      if (serviceName) params.service_name = serviceName;
      
      toast.loading('Exporting data...');
      
      const response = await axios.get(`${API}/admin/payments/export`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Parse CSV to get structured data
      const csvData = response.data.csv;
      const lines = csvData.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const rows = lines.slice(1).map(line => {
        const values = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.replace(/"/g, '').trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.replace(/"/g, '').trim());
        return values;
      });
      
      // Generate filename
      const baseFilename = response.data.filename.replace('.csv', '');
      
      if (format === 'csv') {
        exportCSV(csvData, baseFilename);
      } else if (format === 'excel') {
        exportExcel(headers, rows, baseFilename);
      } else if (format === 'pdf') {
        exportPDF(headers, rows, baseFilename);
      } else if (format === 'print') {
        printData(headers, rows);
      }
      
      toast.dismiss();
      toast.success(`Exported ${rows.length} payments successfully!`);
    } catch (error) {
      console.error('Error exporting payments:', error);
      toast.dismiss();
      toast.error('Failed to export payments');
    } finally {
      setExporting(false);
    }
  };

  const exportCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportExcel = (headers, rows, filename) => {
    // Create HTML table for Excel
    const tableHtml = `
      <table>
        <thead>
          <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
        </thead>
        <tbody>
          ${rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
        </tbody>
      </table>
    `;

    const blob = new Blob([tableHtml], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPDF = (headers, rows, filename) => {
    // Create printable content
    const content = `
      <html>
        <head>
          <title>${filename}</title>
          <style>
            body { font-family: Arial, sans-serif; }
            table { width: 100%; border-collapse: collapse; font-size: 8px; }
            th, td { border: 1px solid #ddd; padding: 4px; text-align: left; }
            th { background-color: #7c3aed; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            h1 { text-align: center; color: #7c3aed; }
          </style>
        </head>
        <body>
          <h1>Payment Report</h1>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    
    // Wait for content to load then trigger print and close
    setTimeout(() => {
      printWindow.print();
      setTimeout(() => printWindow.close(), 100);
    }, 250);
  };

  const printData = (headers, rows) => {
    const content = `
      <html>
        <head>
          <title>Payment Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 9px; }
            th { background-color: #7c3aed; color: white; font-weight: bold; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            h1 { text-align: center; color: #7c3aed; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <h1>Payment Report</h1>
          <table>
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/admin/payments/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment deleted successfully');
      fetchPayments();
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Failed to delete payment');
    }
  };

  const getProductTypeLabel = (type) => {
    const labels = {
      'subscription': 'Platform Subscription',
      'tip_purchase': 'Tip Purchase',
      'service_subscription': 'Service Subscription'
    };
    return labels[type] || type;
  };

  if (!user?.is_admin) {
    return null;
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="ghost"
            className="text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Payment Management</h1>
              <p className="text-gray-400">Track all payments, revenue, and tipster earnings</p>
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="border-white/10 text-white hover:bg-white/5"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <Card className="glass-dark border-white/10 mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label className="text-gray-300 mb-2">From Date</Label>
                  <DateInput
                    value={fromDate}
                    onChange={setFromDate}
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2">To Date</Label>
                  <DateInput
                    value={toDate}
                    onChange={setToDate}
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2">Product Type</Label>
                  <Select value={productType || "all"} onValueChange={(val) => setProductType(val === "all" ? "" : val)}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue placeholder="All Products" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/10 text-white">
                      <SelectItem value="all" className="text-white">All Products</SelectItem>
                      <SelectItem value="subscription_premium" className="text-white">Premium Subscription</SelectItem>
                      <SelectItem value="subscription_tipster" className="text-white">Tipster Subscription</SelectItem>
                      <SelectItem value="tip_purchase" className="text-white">Tip Purchase</SelectItem>
                      <SelectItem value="service_subscription" className="text-white">Service Subscription</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300 mb-2">Customer Email</Label>
                  <Input
                    type="text"
                    placeholder="Search by customer email"
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2">Tipster/Owner Email</Label>
                  <Input
                    type="text"
                    placeholder="Search by tipster email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2">Service Name</Label>
                  <Input
                    type="text"
                    placeholder="Search by service name"
                    value={serviceName}
                    onChange={(e) => setServiceName(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleApplyFilters} className="bg-blue-600 hover:bg-blue-700">
                  Apply Filters
                </Button>
                <Button onClick={handleClearFilters} variant="outline" className="border-white/10 text-white hover:bg-white/5">
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
                
                {/* Export Menu */}
                <div className="relative ml-auto">
                  <Button 
                    data-testid="export-menu-button"
                    onClick={() => setShowExportMenu(!showExportMenu)}
                    disabled={exporting || payments.length === 0}
                    className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    {exporting ? 'Exporting...' : 'Export Data'}
                    {!exporting && <ChevronDown className="w-4 h-4" />}
                  </Button>
                  
                  {showExportMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-white/10 rounded-lg shadow-xl z-50">
                      <div className="py-1">
                        <button
                          onClick={() => handleExport('csv')}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 flex items-center gap-3"
                        >
                          <FileText className="w-4 h-4" />
                          Export as CSV
                        </button>
                        <button
                          onClick={() => handleExport('excel')}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 flex items-center gap-3"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                          Export as Excel
                        </button>
                        <button
                          onClick={() => handleExport('pdf')}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 flex items-center gap-3"
                        >
                          <FileText className="w-4 h-4" />
                          Export as PDF
                        </button>
                        <button
                          onClick={() => handleExport('print')}
                          className="w-full px-4 py-2 text-left text-white hover:bg-white/10 flex items-center gap-3"
                        >
                          <Printer className="w-4 h-4" />
                          Print Report
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <Card className="bg-gradient-to-br from-green-500/20 to-green-700/20 border-green-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <DollarSign className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Total Revenue</p>
                    <p className="text-2xl font-bold text-white">
                      <Price amount={statistics.total_revenue} />
                    </p>
                    <p className="text-xs text-gray-500">{statistics.total_payments} payments</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-500/20 to-blue-700/20 border-blue-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Platform Fees</p>
                    <p className="text-2xl font-bold text-white">
                      <Price amount={statistics.platform_fees_collected} />
                    </p>
                    <p className="text-xs text-gray-500">{statistics.platform_fee_percentage}% fee</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/20 to-purple-700/20 border-purple-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-500/20 rounded-lg">
                    <Users className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Total Owed (Gross)</p>
                    <p className="text-2xl font-bold text-white">
                      <Price amount={statistics.total_owed_to_tipsters} />
                    </p>
                    <p className="text-xs text-gray-500">To tipsters (before withdrawals)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500/20 to-orange-700/20 border-orange-500/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-500/20 rounded-lg">
                    <CreditCard className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Net Owed</p>
                    <p className="text-2xl font-bold text-white">
                      <Price amount={statistics.net_owed_to_tipsters} />
                    </p>
                    <p className="text-xs text-gray-500">After <Price amount={statistics.total_withdrawn} /> withdrawn</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Revenue Breakdown */}
        {statistics && (
          <Card className="glass-dark border-white/10 mb-6">
            <CardHeader>
              <CardTitle className="text-white">Revenue by Product</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-1">Premium Subscriptions</p>
                  <p className="text-xl font-bold text-white">
                    <Price amount={statistics.premium_subscription_revenue} />
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-1">Tipster Subscriptions</p>
                  <p className="text-xl font-bold text-white">
                    <Price amount={statistics.tipster_subscription_revenue} />
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-1">Tip Purchases</p>
                  <p className="text-xl font-bold text-white">
                    <Price amount={statistics.tip_purchase_revenue} />
                  </p>
                </div>
                <div className="bg-white/5 rounded-lg p-4">
                  <p className="text-sm text-gray-400 mb-1">Service Subscriptions</p>
                  <p className="text-xl font-bold text-white">
                    <Price amount={statistics.service_subscription_revenue} />
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment List */}
        <Card className="glass-dark border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Recent Payments</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
                <p className="text-gray-400 mt-4">Loading payments...</p>
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                No payments found
              </div>
            ) : (
              <div className="space-y-2">
                {payments.map((payment) => (
                  <div key={payment.id} className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors">
                    {/* Header Row - Service Name (if applicable) */}
                    {payment.tipster_info && (
                      <div className="mb-3 pb-3 border-b border-white/10">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-xs text-purple-400 mb-1">TIP SERVICE</p>
                            <p className="text-lg font-bold text-white">{payment.product_name}</p>
                            <div className="flex items-center gap-4 mt-1">
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-gray-400">Owner:</p>
                                <p className="text-sm font-semibold text-blue-400">{payment.tipster_info.tipster_name}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <p className="text-xs text-gray-400">Email:</p>
                                <p className="text-sm text-blue-300">{payment.tipster_info.tipster_email}</p>
                              </div>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleDeletePayment(payment.id)}
                            variant="destructive"
                            size="sm"
                            className="bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      {/* User Info */}
                      <div>
                        <p className="text-xs text-gray-400">Customer</p>
                        <p className="text-sm font-semibold text-white">{payment.user_name}</p>
                        <p className="text-xs text-gray-500">{payment.user_email}</p>
                      </div>
                      
                      {/* Amount */}
                      <div>
                        <p className="text-xs text-gray-400">Amount</p>
                        <p className="text-lg font-bold text-green-400">
                          <Price amount={payment.amount} />
                        </p>
                        {!payment.tipster_info && (
                          <p className="text-xs text-blue-400">{getProductTypeLabel(payment.product_type)}</p>
                        )}
                      </div>
                      
                      {/* Dates */}
                      <div>
                        <p className="text-xs text-gray-400">Payment Date</p>
                        <p className="text-sm text-white">{formatDate(payment.payment_date)}</p>
                        {payment.expire_date && (
                          <p className="text-xs text-gray-500">Expires: {formatDate(payment.expire_date)}</p>
                        )}
                      </div>
                      
                      {/* Tipster Earnings */}
                      <div>
                        {payment.tipster_info ? (
                          <>
                            <p className="text-xs text-gray-400">Tipster Earnings</p>
                            <p className="text-sm font-semibold text-purple-400">
                              <Price amount={payment.tipster_info.tipster_earnings} />
                            </p>
                            <p className="text-xs text-gray-500">
                              Platform Fee: <Price amount={payment.tipster_info.platform_fee} />
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-xs text-gray-400">Platform Revenue</p>
                            <p className="text-sm font-semibold text-green-400">
                              <Price amount={payment.amount} />
                            </p>
                            <p className="text-xs text-gray-500">100% Platform</p>
                          </>
                        )}
                      </div>
                      
                      {/* Actions - only show for platform payments */}
                      {!payment.tipster_info && (
                        <div className="flex items-center justify-end">
                          <Button
                            onClick={() => handleDeletePayment(payment.id)}
                            variant="destructive"
                            size="sm"
                            className="bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-500/30"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/10">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/10"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>
                <span className="text-sm text-gray-400">
                  Page {pagination.current_page} of {pagination.total_pages} ({pagination.total_items} total)
                </span>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(pagination.total_pages, prev + 1))}
                  disabled={currentPage === pagination.total_pages}
                  variant="outline"
                  className="border-white/10 text-white hover:bg-white/10"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </Layout>
  );
};

export default AdminPayments;
